// src/app/modules/commissionClient/apis/requestsApi.ts

import axios, { AxiosResponse } from 'axios'
import { getTokenConfig } from '../../../../../src/auth'
import {
  ClientCommissionItem,
  CustomerItem
} from '../core/CommissionClient.model'

const BASE = `${process.env.REACT_APP_API_URL}`

// ==== helpers ====
const parseNum = (v: any): number => {
  if (v === null || v === undefined) return NaN
  if (typeof v === 'number') return v
  if (typeof v === 'string') return parseFloat(v.replace(',', '.'))
  return NaN
}
const to4 = (n: number) =>
  Number.isFinite(n) ? Number.parseFloat(n.toFixed(4)) : n

/** 1) Listado de clientes para el Select */
export async function fetchCustomers(): Promise<CustomerItem[]> {
  const cfg = await getTokenConfig()
  const res: AxiosResponse<{ data: { customers: CustomerItem[] } }> =
    await axios.get(`${BASE}/admin/customers-payment-platform`, cfg)
  return res.data.data.customers
}

/** Shape que puede devolver el backend (legacy y nuevo) */
type OverrideAPI = {
  customerId: number
  techPartner: string

  // NUEVO esquema:
  comissionBaseTech?: string | number
  isPercentageTech?: boolean
  comissionBaseCommercial?: string | number
  isPercentageCommercial?: boolean

  // LEGACY (solo porcentajes):
  pctBaseTech?: string | number
  pctBaseCommercial?: string | number

  // opcional
  isDefault?: boolean
  updatedAt?: string
}

/** 2) Trae los overrides de un cliente y los mapea a ClientCommissionItem */
export async function fetchClientCommissions(
  customerId: number
): Promise<ClientCommissionItem[]> {
  const cfg = await getTokenConfig()
  const res: AxiosResponse<{ data: { commissionOverrides: OverrideAPI[] } }> =
    await axios.get(
      `${BASE}/customers/${customerId}/partners/commission-default`,
      cfg
    )

  const rows = res.data.data.commissionOverrides ?? []

  return rows.map((o) => {
    // prioriza nuevo esquema; si no, cae a legacy
    const tech = parseNum(
      o.comissionBaseTech !== undefined ? o.comissionBaseTech : o.pctBaseTech
    )
    const comm = parseNum(
      o.comissionBaseCommercial !== undefined ? o.comissionBaseCommercial : o.pctBaseCommercial
    )

    const isPctTech =
      o.isPercentageTech !== undefined ? !!o.isPercentageTech : true
    const isPctComm =
      o.isPercentageCommercial !== undefined ? !!o.isPercentageCommercial : true

    return {
      customerId : o.customerId,
      techPartner: o.techPartner,

      comissionBaseTech      : to4(tech),
      isPercentageTech       : isPctTech,
      comissionBaseCommercial: to4(comm),
      isPercentageCommercial : isPctComm,

      isDefault: !!o.isDefault,
    }
  })
}

/** 3) Guarda/actualiza los overrides (nuevo esquema con flags y 4 decimales) */
export async function saveClientCommissions(
  customerId: number,
  overrides: ClientCommissionItem[]
): Promise<void> {
  const cfg = await getTokenConfig()

  // normaliza/recorta a 4 decimales antes de enviar
  const payload = {
    commissionOverrides: overrides.map((o) => ({
      customerId: o.customerId,
      techPartner: o.techPartner,

      comissionBaseTech      : to4(o.comissionBaseTech),
      isPercentageTech       : !!o.isPercentageTech,
      comissionBaseCommercial: to4(o.comissionBaseCommercial),
      isPercentageCommercial : !!o.isPercentageCommercial,

      isDefault: !!o.isDefault,
    })),
  }

  const res = await axios.put(
    `${BASE}/customers/${customerId}/partners/commission-default`,
    payload,
    cfg
  )
  if (res.status !== 200) {
    throw new Error('Error al guardar las comisiones personalizadas')
  }
}
